import { Component, Input, Output, EventEmitter, signal, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface DropdownOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-shared-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="dropdown-root">
      <button 
        class="filter-box origin-box" 
        [ngClass]="buttonClass"
        type="button"
        [disabled]="disabled"
        [attr.aria-expanded]="isOpen()"
        (click)="toggleDropdown($event)"
        (keydown.enter)="toggleDropdown($event)"
        (keydown.space)="$event.preventDefault(); toggleDropdown($event)">
        <span class="filter-input">{{ selectedLabel || placeholder }}</span>
        <img class="filter-chevron" src="/assets/images/Filters_Chevron_Icon.png" alt="open">
      </button>
      
      <div class="dropdown-panel" *ngIf="isOpen()" 
           (click)="$event.stopPropagation()"
           (keydown)="$event.stopPropagation()"
           role="menu">
        
        <!-- Search input for searchable dropdowns -->
        <div class="dropdown-search-row" *ngIf="searchable">
          <label [for]="searchInputId" class="sr-only">Search {{ label }}</label>
          <input 
            [id]="searchInputId" 
            class="dropdown-search" 
            type="text" 
            placeholder="Search" 
            [(ngModel)]="searchTerm" 
            autocomplete="off">
          <button class="dropdown-close" type="button" (click)="closeDropdown()">×</button>
        </div>
        
        <!-- Options list -->
        <div class="dropdown-options">
          <button 
            class="option-btn" 
            type="button" 
            *ngFor="let option of filteredOptions; trackBy: trackOption"
            (click)="selectOption(option)">
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./shared-dropdown.component.css']
})
export class SharedDropdownComponent {
  @Input() options: DropdownOption[] = [];
  @Input() selectedValue: string | null = null;
  @Input() placeholder = 'Select';
  @Input() label = '';
  @Input() searchable = false;
  @Input() buttonClass = '';
  @Input() disabled = false;

  @Output() selectionChange = new EventEmitter<string>();
  @Output() dropdownToggle = new EventEmitter<boolean>();

  isOpen = signal(false);
  searchTerm = '';

  get searchInputId(): string {
    return `${this.label.toLowerCase().replaceAll(/\s+/g, '-')}-dropdown-search`;
  }

  get selectedLabel(): string {
    const selected = this.options.find(opt => opt.value === this.selectedValue);
    return selected?.label || '';
  }

  get filteredOptions(): DropdownOption[] {
    if (!this.searchable || !this.searchTerm) {
      return this.options;
    }
    
    const searchLower = this.searchTerm.toLowerCase();
    return this.options.filter(option => 
      option.label.toLowerCase().includes(searchLower) ||
      option.value.toLowerCase().includes(searchLower)
    );
  }

  trackOption(_: number, option: DropdownOption): string {
    return option.value;
  }

  constructor(private readonly elementRef: ElementRef) {}

  toggleDropdown(event: Event): void {
    if (this.disabled) return;
    
    event.stopPropagation();
    this.isOpen.update(open => !open);
    this.dropdownToggle.emit(this.isOpen());
    
    if (!this.isOpen()) {
      this.searchTerm = '';
    }
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    this.searchTerm = '';
    this.dropdownToggle.emit(false);
  }

  selectOption(option: DropdownOption): void {
    this.selectedValue = option.value;
    this.selectionChange.emit(option.value);
    this.closeDropdown();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }
}